import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Layout from "@/components/app/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import ApplicationForm from "@/pages/ApplicationForm";
import AcceptInvite from "@/pages/AcceptInvite";
import Plans from "@/pages/Plans";
import { useLocation } from "react-router-dom";
import ClientLogin from "@/pages/ClientLogin";
import ClientPortal, { ClientProjectDetail } from "@/pages/ClientPortal";
import { TracLogin, TracShell, TracHome, TracProjects, TracProjectDetail, TracMessages, TracFiles } from "@/pages/Trac";
import {
  StaffLogin, StaffShell, StaffHome, StaffWorkOrders, StaffWorkOrderDetail,
  StaffProjects, StaffProjectDetail, StaffTickets, StaffTicketDetail,
  StaffFiles, StaffMessages, StaffTasks,
} from "@/pages/Staff";
import Dashboard from "@/pages/Dashboard";
import Freelancers from "@/pages/Freelancers";
import FreelancerDetail from "@/pages/FreelancerDetail";
import WorkOrders from "@/pages/WorkOrders";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Payments from "@/pages/Payments";
import Performance from "@/pages/Performance";
import Analytics from "@/pages/Analytics";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import ManagementTasks from "@/pages/ManagementTasks";
import Clients from "@/pages/Clients";
import Services from "@/pages/Services";
import AutomationSettings from "@/pages/AutomationSettings";
import ManagementAnalytics from "@/pages/ManagementAnalytics";
import Vendors from "@/pages/Vendors";
import VendorDetail from "@/pages/VendorDetail";
import ManagerEodReport from "@/pages/ManagerEod";

function AppRouter() {
  const location = useLocation();
  // Detect the Google Auth session_id fragment synchronously during render so we
  // process it before AuthProvider's /me check races us.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/billing" element={<Navigate to="/dashboard" replace />} />
      <Route path="/paywall" element={<Navigate to="/dashboard" replace />} />
      <Route path="/trial-extend/*" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/apply" element={<ApplicationForm />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client" element={<ClientPortal />} />
      <Route path="/client/projects/:id" element={<ClientProjectDetail />} />

      {/* Mariea Connect — mobile PWA for Dr. Mariea's Publication and Research */}
      <Route path="/trac/login" element={<TracLogin />} />
      <Route path="/trac" element={<TracShell />}>
        <Route index element={<TracHome />} />
        <Route path="projects" element={<TracProjects />} />
        <Route path="projects/:id" element={<TracProjectDetail />} />
        <Route path="messages" element={<TracMessages />} />
        <Route path="files" element={<TracFiles />} />
        <Route path="invoices" element={<Navigate to="/trac" replace />} />
      </Route>

      {/* FreLancTrac — internal staff PWA (Freelancer / Manager / Operation Head / SuperAdmin) */}
      <Route path="/app/login" element={<StaffLogin />} />
      <Route path="/app" element={<StaffShell />}>
        <Route index element={<StaffHome />} />
        <Route path="work-orders" element={<StaffWorkOrders />} />
        <Route path="work-orders/:id" element={<StaffWorkOrderDetail />} />
        <Route path="projects" element={<StaffProjects />} />
        <Route path="projects/:id" element={<StaffProjectDetail />} />
        <Route path="tickets" element={<StaffTickets />} />
        <Route path="tickets/:id" element={<StaffTicketDetail />} />
        <Route path="files" element={<StaffFiles />} />
        <Route path="messages" element={<StaffMessages />} />
        <Route path="tasks" element={<StaffTasks />} />
      </Route>

      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />        <Route path="/freelancers" element={<ProtectedRoute managerOnly><Freelancers /></ProtectedRoute>} />
        <Route path="/freelancers/:id" element={<FreelancerDetail />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/analytics" element={<ProtectedRoute managerOnly><Analytics /></ProtectedRoute>} />
        <Route path="/rate-cards" element={<ProtectedRoute managerOnly><Pricing /></ProtectedRoute>} />
        <Route path="/tasks" element={<ManagementTasks />} />
        <Route path="/clients" element={<ProtectedRoute managerOnly><Clients /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute managerOnly><Services /></ProtectedRoute>} />
        <Route path="/automation-settings" element={<ProtectedRoute managerOnly><AutomationSettings /></ProtectedRoute>} />
        <Route path="/management-analytics" element={<ProtectedRoute adminOrLeadOnly><ManagementAnalytics /></ProtectedRoute>} />
        <Route path="/vendors" element={<ProtectedRoute managerOnly><Vendors /></ProtectedRoute>} />
        <Route path="/vendors/:id" element={<ProtectedRoute managerOnly><VendorDetail /></ProtectedRoute>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/users" element={<ProtectedRoute adminOrLeadOnly><Users /></ProtectedRoute>} />
      </Route>
      <Route path="/dashboard/report" element={<ProtectedRoute><ManagerEodReport bare /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ classNames: { toast: "border border-black/10 rounded-2xl bg-white text-ink shadow-lg" } }} />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
